import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  ListItemText,
  Avatar,
} from '@mui/material';
import { Brightness4, Brightness7, Person, Logout } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import CheckForUpdatesButton from './CheckForUpdatesButton';

interface User {
  email?: string | null;
  image?: string | null;
  name?: string | null;
}

interface UserMenuProps {
  user: User;
  mode: string;
  logout: () => void;
  toggleTheme: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({
  user,
  mode,
  logout,
  toggleTheme,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  return (
    <>
      <Tooltip title="Account settings">
        <IconButton
          onClick={handleClick}
          size="medium"
          edge="end"
          name="accountSettings"
          aria-label="Account Settings"
          aria-controls="menu-appbar"
          aria-haspopup="true"
          color="inherit"
        >
          <Avatar
            src={user?.image || ''}
            sizes="large"
            alt="user-avatar"
            sx={{ width: { xs: 28, sm: 37 }, height: { xs: 28, sm: 37 } }}
          />
        </IconButton>
      </Tooltip>

      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem
          title="Open Profile"
          aria-label="Open Profile"
          onClick={() => {
            handleClose();
            navigate('/profile');
          }}
        >
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={user.email} />
        </MenuItem>

        <MenuItem onClick={toggleTheme}>
          <ListItemIcon>
            {mode === 'dark' ? (
              <Brightness7 fontSize="small" />
            ) : (
              <Brightness4 fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
          />
        </MenuItem>
        <CheckForUpdatesButton asMenuItem onTriggered={handleClose} />
        <MenuItem onClick={handleLogout} aria-label="Logout" title="Logout">
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserMenu;
